import { Component, OnInit, Input, SimpleChanges, OnChanges, OnDestroy, Type } from '@angular/core';
import { Subscription, Subject, of as observabelOf, Observable } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { map, take, catchError, filter } from 'rxjs/operators';

import { IntralinksService } from '../../tamalelibs/services/intralinks.service';
import {
    IntralinksGroupReloadAction, IntralinksGroupConfig, IntralinksGroupContainerConfig,
    IntralinksGroupContainerParams, IntralinksGroupContainerReadyFeedback, IntralinksGroupReadyFeedback,
    IntralinksGroupFeedbackType, IntralinksGroupContainerActionType, IntralinksGroupUpdateAction,
    IntralinksGroupAction, IntralinksGroupContainerAction
} from './intralinks-group.model';
import { NotificationOptions, NotificationStyles } from '../../widgets/notification/notification.model';
import { ToastService } from './../../widgets/toast/toast.service';
import { IntralinksFile } from '../../tamalelibs/models/intralinks.model';
import { businessConstants } from '../../constants/business.constants';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';

@Component({
    selector: 'tam-intralinks-group-container',
    templateUrl: './intralinks-group-container.component.html',
    styleUrls: ['./intralinks-group-container.component.scss']
}

) export class IntralinksGroupContainerComponent implements OnInit, OnDestroy, OnChanges {

    @Input() config: IntralinksGroupContainerConfig;
    timeValue = { data: '', text: '' };
    timeUnit = { data: '', text: '' };
    keyword: string;
    workspaceId: string;
    screenId: string;

    groupConfigs: IntralinksGroupConfig[] = null;
    timeValueOptions = [];
    timeUnitOptions = [];
    reloading = false;
    groupActionSubject$ = new Subject<IntralinksGroupAction>();
    groupFeedbackSubject$ = new Subject<IntralinksGroupReadyFeedback>();
    readyCountdown = 0;
    loadCountdown = 0;
    noData = false;
    recordCount = 0;
    loaderCount = 0;
    maxParallelCalls = 5;
    private _waitingSubscriptions: Array<Subscription> = [];
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _intralinksService: IntralinksService,
        private _toast: ToastService
    ) { }

    ngOnInit() {
        for (let i = 1; i < 12; i++) {
            this.timeValueOptions.push({ data: i.toString(), text: i.toString() });
        }
        this.timeUnitOptions = [{ data: 'd', text: 'Days' }, { data: 'm', text: 'Months' }, { data: 'y', text: 'Years' }];
        this._destroySubscriptions.push(
            this.groupFeedbackSubject$.pipe(filter(feedback => feedback.type === IntralinksGroupFeedbackType.READY))
                .subscribe((feedback: IntralinksGroupReadyFeedback) => {
                    setTimeout(() => {
                        this.readyCountdown--;
                        if (this.readyCountdown === 0) {
                            this.config.feedbackSubject$.next(new IntralinksGroupContainerReadyFeedback());
                        }
                    });
                }));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.config.currentValue && changes.config.currentValue.folders && changes.config.currentValue.folders.length) {
            this._destroySubscriptions.push(
                changes.config.currentValue.actionSubject$.subscribe((action: IntralinksGroupContainerAction) => {
                    if (action.type === IntralinksGroupContainerActionType.RELOAD) {
                        setTimeout(() => {
                            this._onReload(action.payload);
                        });
                    } else if (action.type === IntralinksGroupContainerActionType.UPDATE_FILE) {
                        this._onUpdateFile(action.payload);
                    }
                }));
            setTimeout(() => {
                this.recordCount = 0;
                this.groupConfigs = this._generateGroupConfigs(changes.config.currentValue.folders);
                this.readyCountdown = this.groupConfigs.length;
                this.loadCountdown = this.groupConfigs.length;
                this.noData = false;
            });
        }
    }

    private _onReload(params: IntralinksGroupContainerParams): void {
        this._recycleSubscriptions(this._waitingSubscriptions);
        const values = this.timeValueOptions.filter(item => item.data === params.timeValue);
        if (values && values.length > 0) {
            this.timeValue = values[0];
        } else {
            this._router.navigate(['/error']);
        }
        const units = this.timeUnitOptions.filter(item => item.data === params.timeUnit);
        if (units && units.length > 0) {
            this.timeUnit = units[0];
        } else {
            this._router.navigate(['/error']);
        }
        this.keyword = params.keyword;
        this.workspaceId = params.workspaceId;
        this.screenId = params.screenId;
        const action = new IntralinksGroupReloadAction();
        this.loaderCount = 0;
        action.payload = this.getReloader();
        this.groupActionSubject$.next(action);
    }
    private _onUpdateFile(file: IntralinksFile) {
        const action = new IntralinksGroupUpdateAction();
        action.payload = file;
        this.groupActionSubject$.next(action);
    }

    ngOnDestroy(): void {
        this._recycleSubscriptions(this._waitingSubscriptions);
        this._recycleSubscriptions(this._destroySubscriptions);
    }

    private _recycleSubscriptions(subscriptions: Subscription[]) {
        subscriptions.forEach(subscription => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
        subscriptions = [];
    }

    onFilterChange(workflow, event) {
        const rootPaths = this.workspaceId ? ['folder', this.workspaceId] : [];
        switch (workflow) {
            case 'value':
                this._router.navigate([...rootPaths, 'recents', event.data + this.timeUnit.data], { relativeTo: this._activatedRoute });
                break;
            case 'unit':
                this._router.navigate([...rootPaths, 'recents', this.timeValue.data + event.data], { relativeTo: this._activatedRoute });
                break;
            case 'keyword':
                this._router.navigate([...rootPaths, 'recents', this.timeValue.data + this.timeUnit.data], { relativeTo: this._activatedRoute });
                break;
        }
    }

    getReloader(): (id: string, workspaceId: string) => Observable<IntralinksFile[]> {
        return (id, workspaceId) => {
            this.loaderCount++;
            let hasWaited = 0;
            const index = this.loaderCount;
            if (index < this.maxParallelCalls) {
                return this._loadFolderContent(workspaceId, id);
            } else {
                return new Observable((observer) => {
                    const waitingSubscription = this.groupFeedbackSubject$.pipe(
                        filter(feedback => feedback.type === IntralinksGroupFeedbackType.LOADED)
                    ).subscribe((feedback) => {
                        hasWaited++;
                        if (index - hasWaited < this.maxParallelCalls) {
                            waitingSubscription.unsubscribe();
                            this._loadFolderContent(workspaceId, id).pipe(take(1)).subscribe((files) => {
                                observer.next(files);
                            });
                        }
                    });
                    this._waitingSubscriptions.push(waitingSubscription);
                });
            }

        };
    }

    private _loadFolderContent(workspaceId, folderId): Observable<any[]> {
        return this._intralinksService
            .getContentOfFolder(this.screenId, workspaceId, folderId, this.timeValue.data + this.timeUnit.data)
            .pipe(
                catchError(this._errorHandler.bind(this)),
                map((response) => {
                    const files = this._intralinksService.treeFileMapper(response, folderId, this.config?.mappings).filter((file) => file.parentId === folderId);
                    this.recordCount += (files ? files.length : 0);
                    this.loadCountdown--;
                    if (this.loadCountdown === 0) {
                        this.noData = this.recordCount === 0;
                    }
                    return files;
                })
            );
    }

    private _generateGroupConfigs(folders: any[], parentNames: string[] = []): IntralinksGroupConfig[] {
        let result: IntralinksGroupConfig[] = [];
        if (folders) {
            folders.forEach((folder) => {
                if (folder.folderType !== 'link') {
                    const names = [...parentNames];
                    names.push(folder.displayText);
                    if (folder.folderType === 'folder') {
                        const groupConfig = new IntralinksGroupConfig();
                        groupConfig.id = folder.folderId;
                        groupConfig.workspaceId = folder.workspaceId;
                        groupConfig.actionSubject$ = this.groupActionSubject$;
                        groupConfig.feedbackSubject$ = this.groupFeedbackSubject$;
                        groupConfig.name = names.join('>');
                        groupConfig.showWorkspaceName = this.config.showWorkspaceName;
                        result.push(groupConfig);
                    }
                    const subResult = this._generateGroupConfigs(folder.items, names);
                    result = [...result, ...subResult];
                }
            });
        }
        // return result.slice(0, 10);
        return result;
    }

    private _errorHandler(err) {
        const note = new NotificationOptions();
        note.style = NotificationStyles.Error;
        if (err.includes(businessConstants.errorMessage.ERR_INTRALINKS_SERVER)) {
            note.message = StringLiteralsPipe.translate('general.the_configuration_is_not_correct');
        } else {
            note.message = err;
        }
        this._toast.notify(note);
        return observabelOf([]);
    }

}
